import React, { ChangeEvent } from 'react';
import {
  GadgetFormInner,
  GadgetFormInnerContent,
  GadgetFormWrapper,
} from '../V5GadgetForm/V5GadgetForm';
import Typography from 'kingpin/atoms/Typography';
import toSentenceCase from 'services/toSentenceCase';
import { Section } from '../MyAccount/Profile/styles';
import Inputs from '../Inputs';
import RankingMetricInput from './RankingMetricInput';
import AddRankingMetricButton from './AddRankingMetricButton';
import RankingMatrixSortingInput from './RankingMatrixSortingInput';

const INTERVAL_OPTIONS: (FleetOps.Interval | FiscalInterval)[] = [
  'auto',
  'day',
  'week',
  'month',
  'quarter',
  'year',
  'fiscalPeriod',
  'fiscalQuarter',
  'fiscalYear',
];

const FISCAL_INTERVALS = ['fiscalPeriod', 'fiscalQuarter', 'fiscalYear'];

const RankingMatrixForm = ({
  name,
  description,
  metrics,
  updateMetric,
  onMetricAdded,
  removeMetric,
  groupByField,
  groupingOptions,
  isDateGrouping,
  interval,
  setInterval,
  isFiscalDisabled,
  sorting,
  setSorting,
  gridSizingOptions,
  onNameChanged,
  onDescriptionChanged,
  isEditing,
  isLoading,
}: {
  // Base
  name: string;
  onNameChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  description: string;
  onDescriptionChanged: (event: ChangeEvent<HTMLInputElement>) => void;
  // Ranking Matrix
  metrics: VisualisationDefinitions.RankingMatrixMetric[];
  updateMetric: (
    newMetric: VisualisationDefinitions.RankingMatrixMetric,
  ) => void;
  onMetricAdded: (metric: Metrics.Metric) => void;
  removeMetric: (
    removedMetric: VisualisationDefinitions.RankingMatrixMetric,
  ) => void;
  groupByField?: string;
  groupingOptions: DropdownOption[];
  isDateGrouping: boolean;
  interval: FleetOps.Interval | FiscalInterval;
  setInterval: React.Dispatch<
    React.SetStateAction<FleetOps.Interval | FiscalInterval>
  >;
  isFiscalDisabled: boolean;
  sorting: VisualisationDefinitions.RankingMatrixSorting;
  setSorting: React.Dispatch<
    React.SetStateAction<VisualisationDefinitions.RankingMatrixSorting>
  >;
  gridSizingOptions: DropdownOption[];
  // Misc
  isEditing: boolean;
  isLoading: boolean;
}) => (
  <GadgetFormWrapper>
    <GadgetFormInner isGadgetBuilder={false}>
      <GadgetFormInnerContent isGadgetBuilder={false}>
        <Section>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Name</Typography.Body>
          </div>
          <Inputs.TextInput
            placeholder="Name"
            value={name}
            onChange={onNameChanged}
            data-testid="name"
          />
        </Section>
        <Section>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Description</Typography.Body>
          </div>
          <Inputs.TextInput
            placeholder="Description"
            value={description}
            onChange={onDescriptionChanged}
          />
        </Section>
        <Section>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Metrics</Typography.Body>
          </div>
          {metrics.map((metric) => (
            <RankingMetricInput
              key={metric.key}
              metric={metric}
              updateMetric={updateMetric}
              removeMetric={removeMetric}
              groupByField={groupByField}
            />
          ))}
          <AddRankingMetricButton
            onMetricAdded={onMetricAdded}
            groupByField={groupByField}
          />
        </Section>
        <Section>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Grouping</Typography.Body>
          </div>
          <Inputs.Dropdown
            isSearchEnabled
            isDisabled={groupingOptions.length === 0}
            options={groupingOptions}
            selectedLabel={groupByField}
            testId="grouping"
          />
        </Section>
        {isDateGrouping && (
          <Section>
            <div style={{ marginBottom: 4 }}>
              <Typography.Body type="Label">Interval</Typography.Body>
            </div>
            <Inputs.Dropdown
              options={INTERVAL_OPTIONS.map((i) => ({
                label: toSentenceCase(i),
                isDisabled: FISCAL_INTERVALS.includes(i) && isFiscalDisabled,
                onSelected: () => {
                  setInterval(i);
                },
              }))}
              selectedLabel={toSentenceCase(interval)}
              testId="interval"
            />
          </Section>
        )}
        <Section>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Sorting</Typography.Body>
          </div>
          <RankingMatrixSortingInput
            sorting={sorting}
            setSorting={setSorting}
            metrics={metrics}
          />
        </Section>
        <Section>
          <div style={{ marginBottom: 4 }}>
            <Typography.Body type="Label">Column Sizing</Typography.Body>
          </div>
          <Inputs.Dropdown options={gridSizingOptions} />
        </Section>
      </GadgetFormInnerContent>
    </GadgetFormInner>
  </GadgetFormWrapper>
);

export default RankingMatrixForm;
